'use client';

import Image from 'next/image';
import { useInView, useScroll } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { getMobileHighlightActiveCount } from '../highlight.constants';
import poster from '@images/main/main-highlight-poster.webp';
import { useStoryReached } from './useStoryReached.hook';
import { useVideoScrub } from './useVideoScrub.hook';
import { HighlightContent } from './highlight-content';

export function MobileHighlight({ usePoster }: { usePoster: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const storyReached = useStoryReached();
  const inView = useInView(ref, { margin: '300px', once: true });
  const nearViewport = storyReached || inView;
  const [activeCount, setActiveCount] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ['start 85%', 'end 95%'],
  });
  const videoRef = useVideoScrub(
    scrollYProgress,
    nearViewport && !videoFailed && !usePoster,
  );

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    let frame: number | undefined;
    const sync = () => {
      frame = undefined;
      const rects = Array.from(list.children, item =>
        item.getBoundingClientRect(),
      );
      setActiveCount(getMobileHighlightActiveCount(rects, window.innerHeight));
    };
    const schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={ref}>
      <HighlightContent
        listRef={listRef}
        activeCount={activeCount}
        media={
          usePoster || videoFailed ? (
            <Image src={poster} alt="" sizes="240px" />
          ) : (
            <video
              ref={videoRef}
              src={nearViewport ? '/videos/main-highlight-720.mp4' : undefined}
              poster={poster.src}
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
              onError={() => setVideoFailed(true)}
            />
          )
        }
      />
    </div>
  );
}
